/**
 * Detects non-actionable ReferenceErrors caused by browser extensions injecting
 * code into the page that references undeclared global variables.
 *
 * Common culprits:
 * - **DarkReader** — a popular dark-mode extension for Safari/Chrome/Firefox
 *   that injects a script referencing a `DarkReader` global.
 * - **BigInt64Array** — polyfill probes from extensions on older WebKit builds.
 * - **Safari autofill/translate** — iOS injected scripts that reference
 *   undeclared variables with no recoverable stack frames.
 *
 * These errors share a distinctive signature:
 * - Message matches "Can't find variable: X" (Safari) or "X is not defined"
 * - The stack trace either:
 *   (a) contains only top-level "global code @" frames (extension injection), or
 *   (b) has no frames at all (the stack is just the error message itself),
 *       meaning there is nothing actionable to investigate.
 */
export function isBrowserExtensionReferenceErrorNoise(
  message: string | undefined,
  stack: string | undefined,
  kind: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (kind !== "ReferenceError" && !message.startsWith("ReferenceError")) {
    return false;
  }

  const isSafariCantFindVariable = message.includes("Can't find variable:");
  const isChromeNotDefined =
    message.includes("is not defined") && !message.includes(".");

  if (!isSafariCantFindVariable && !isChromeNotDefined) {
    return false;
  }

  if (!stack) {
    return isSafariCantFindVariable;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return isSafariCantFindVariable;
  }

  return frames.every((frame) => frame.includes("global code @"));
}
